import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { reconciliationService } from '../../services/reconciliationService';
import ReconciliationWizard from './ReconciliationWizard';
import ReconciliationEditor from './ReconciliationEditor';
import ReconciliationDeleteWarning from '../../components/reconciliation/ReconciliationDeleteWarning';
import SearchableSelect from '../../components/common/SearchableSelect';
import { formatCurrency } from '../../utils/formatters';
import { useClient } from '../../context/ClientContext';

const {
  FiAlertTriangle,
  FiBriefcase,
  FiCheckCircle,
  FiChevronDown,
  FiEdit2,
  FiFilter,
  FiFolder,
  FiLoader,
  FiPlus,
  FiSearch,
  FiTrash2
} = FiIcons;

const formatDate = (dateString) => {
  if (!dateString) return '—';

  return new Date(`${dateString}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatReconciliationBalance = (value) => {
  const numericValue = Number(value || 0);
  const prefix = numericValue < 0 ? '-' : '';

  return `${prefix}${formatCurrency(Math.abs(numericValue))}`;
};

const isNegativeBalance = (value) => Number(value || 0) < 0;

const isUnbalancedReconciliation = (reconciliation) => (
  reconciliation.status === 'UNBALANCED'
  || Math.abs(
    Number(reconciliation.ending_balance || 0)
    - Number(reconciliation.book_balance || 0)
  ) > 0.009
);

const getAccountGroup = (account) => {
  if (account.id === 'all') return 'All accounts';

  const subType = (account.sub_type || '').toLowerCase();

  if (account.account_type === 'credit_card' || account.type === 'Liability') {
    return 'Credit cards';
  }

  if (subType.includes('cash')) return 'Cash accounts';

  return 'Bank accounts';
};

const accountGroupOrder = {
  'All accounts': 0,
  'Bank accounts': 1,
  'Credit cards': 2,
  'Cash accounts': 3
};

export default function Reconciliation() {
  const { activeClient } = useClient();
  const navigate = useNavigate();
  const [reconciliations, setReconciliations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [editingReconciliation, setEditingReconciliation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccountFilter, setSelectedAccountFilter] = useState('all');

  const loadData = async () => {
    if (!activeClient?.id) {
      setReconciliations([]);
      setAccounts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');

    try {
      const [reconData, accountData] = await Promise.all([
        reconciliationService.getReconciliations(
          { bank_connection_id: selectedAccountFilter },
          activeClient.id
        ),
        reconciliationService.getBankAccounts(activeClient.id)
      ]);

      setReconciliations(reconData);
      setAccounts(accountData);
    } catch (error) {
      setReconciliations([]);
      setAccounts([]);
      setLoadError(error.message || 'Unable to load reconciliation history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeClient?.id, selectedAccountFilter]);

  const handleEdit = async (reconciliation) => {
    if (!['RECONCILED', 'UNBALANCED'].includes(reconciliation.status)) {
      return;
    }

    if (reconciliation.status === 'UNBALANCED') {
      navigate('/accounting/transactions', {
        state: {
          openReconciliation: true,
          manualReconciliation: true,
          accountId: reconciliation.coa_id,
          statementDate: reconciliation.statement_date,
          endingBalance: reconciliation.ending_balance,
          reconciliationId: reconciliation.id
        }
      });

      return;
    }

    setEditingId(reconciliation.id);
    setLoadError('');

    try {
      const transactions = await reconciliationService.getReconciliationTransactions(
        reconciliation.transaction_ids
      );

      setEditingReconciliation({
        ...reconciliation,
        transactions
      });
    } catch (error) {
      setLoadError(error.message || 'Unable to open this reconciliation.');
    } finally {
      setEditingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;

    setDeletingId(deleteCandidate.id);
    setLoadError('');

    try {
      if (deleteCandidate.status === 'UNBALANCED') {
        await reconciliationService.cancelUnbalancedReconciliation(
          deleteCandidate.id
        );
      } else {
        await reconciliationService.undoReconciliation(deleteCandidate.id);
      }

      setDeleteCandidate(null);
      await loadData();
    } catch (error) {
      setLoadError(error.message || 'Unable to remove the reconciliation.');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredReconciliations = reconciliations.filter((reconciliation) => (
    reconciliation.bank_connection?.account_name
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase())
  ));

  const accountFilterOptions = [
    { id: 'all', name: 'All Accounts' },
    ...accounts.map((account) => ({
      ...account,
      name: account.account_name
    }))
  ].sort((firstAccount, secondAccount) => {
    const groupDifference = (
      accountGroupOrder[getAccountGroup(firstAccount)]
      - accountGroupOrder[getAccountGroup(secondAccount)]
    );

    if (groupDifference !== 0) return groupDifference;

    return firstAccount.name.localeCompare(secondAccount.name);
  });

  if (editingReconciliation) {
    return (
      <div className="min-h-screen bg-white p-4 md:p-6">
        <ReconciliationEditor
          reconciliation={editingReconciliation}
          onBack={() => setEditingReconciliation(null)}
          onComplete={async () => {
            setEditingReconciliation(null);
            await loadData();
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-[1440px] bg-[#f8fafc] px-4 py-5 font-sans text-slate-900 sm:px-6 md:px-8 md:py-6">
      <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-[24px] font-semibold tracking-tight text-slate-900 md:text-[26px]">
              Reconciliation History
            </h1>
            <span className="rounded-md border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.1em] text-emerald-600">
              Audit Ready
            </span>
          </div>
          <p className="mt-1 text-[13px] font-normal text-slate-500">
            View and manage past bank statement reconciliations.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsWizardOpen(true)}
          disabled={!activeClient?.id}
          className="flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 text-[12px] font-medium text-white shadow-sm shadow-emerald-200/70 transition-all hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <SafeIcon icon={FiPlus} className="text-base" />
          Reconcile an account
        </button>
      </div>

      {loadError && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5 text-base" />
          <div>
            <p>Reconciliation history could not be loaded.</p>
            <p className="mt-0.5 font-normal text-rose-600">{loadError}</p>
          </div>
          <button
            type="button"
            onClick={loadData}
            className="ml-auto rounded-lg px-2.5 py-1 text-[11px] font-medium hover:bg-rose-100"
          >
            Retry
          </button>
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_3px_14px_rgb(15,23,42,0.035)]">
        <div className="flex flex-col justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-4 py-3.5 lg:flex-row lg:items-center">
          <div className="relative w-full lg:w-[300px]">
            <div className="sr-only">
              <SafeIcon
                icon={FiFolder}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400"
              />
              <select
                value={selectedAccountFilter}
                onChange={(event) => setSelectedAccountFilter(event.target.value)}
                className="h-11 w-full cursor-pointer appearance-none rounded-xl border border-slate-200 bg-white pb-0.5 pl-9 pr-9 pt-3 text-[12px] font-medium text-slate-700 outline-none transition-all focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
              >
                <option value="all">All Accounts</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_name}
                  </option>
                ))}
              </select>
              <SafeIcon
                icon={FiChevronDown}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[15px] text-slate-400"
              />
              <span className="pointer-events-none absolute left-9 top-1.5 text-[8px] font-medium uppercase tracking-[0.12em] text-slate-400">
                Target account
              </span>
            </div>

            <SearchableSelect
              options={accountFilterOptions}
              value={selectedAccountFilter}
              onChange={setSelectedAccountFilter}
              placeholder="Target account"
              searchPlaceholder="Search accounts..."
              emptyMessage="No matching accounts"
              grouped
              getGroup={getAccountGroup}
              getLabel={(account) => account.name}
              getSearchText={(account) => [
                account.name,
                account.account_name,
                account.account_type,
                account.type,
                account.sub_type,
                account.mask
              ]}
              menuWidth={300}
              menuMaxWidth={380}
              menuMaxHeight={320}
            />
          </div>

          <div className="flex w-full items-center gap-2 lg:w-auto">
            <div className="relative w-full lg:w-64">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search history..."
                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-[12px] font-normal text-slate-700 outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
              />
            </div>
            <button
              type="button"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50"
              aria-label="Filter reconciliation history"
              title="Filter reconciliation history"
            >
              <SafeIcon icon={FiFilter} className="text-[18px]" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[840px] border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70">
                <th className="px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 md:px-6">
                  Statement End Date
                </th>
                <th className="px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 md:px-6">
                  Reconciled On
                </th>
                <th className="px-5 py-3 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 md:px-6">
                  Starting Balance
                </th>
                <th className="px-5 py-3 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 md:px-6">
                  Closing Balance
                </th>
                <th className="px-5 py-3 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 md:px-6">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan="5" className="px-6 py-14 text-center">
                    <SafeIcon
                      icon={FiLoader}
                      className="mx-auto mb-2 animate-spin text-2xl text-emerald-500"
                    />
                    <p className="text-[12px] font-medium text-slate-500">
                      Loading history...
                    </p>
                  </td>
                </tr>
              )}

              {!loading && !activeClient?.id && (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center">
                    <SafeIcon
                      icon={FiBriefcase}
                      className="mx-auto mb-3 text-3xl text-slate-300"
                    />
                    <p className="text-[15px] font-medium text-slate-800">
                      Select a business
                    </p>
                    <p className="mt-1 text-[12px] font-normal text-slate-500">
                      Choose a business to view its reconciliation history.
                    </p>
                  </td>
                </tr>
              )}

              {!loading && activeClient?.id && filteredReconciliations.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="mx-auto mb-3 text-3xl text-slate-300"
                    />
                    <p className="text-[15px] font-medium text-slate-800">
                      No reconciliations found
                    </p>
                  </td>
                </tr>
              )}

              {filteredReconciliations.map((reconciliation) => {
                const isUnbalanced = isUnbalancedReconciliation(reconciliation);
                const isEditable = ['RECONCILED', 'UNBALANCED'].includes(
                  reconciliation.status
                );
                const isDeleting = deletingId === reconciliation.id;
                const isEditing = editingId === reconciliation.id;

                return (
                  <tr
                    key={reconciliation.id}
                    className={`group transition-colors ${
                      isUnbalanced
                        ? 'bg-amber-50/75 hover:bg-amber-100/70'
                        : 'hover:bg-slate-50/70'
                    }`}
                  >
                    <td className="px-5 py-4 text-[13px] font-medium text-slate-900 md:px-6">
                      {formatDate(reconciliation.statement_date)}
                    </td>

                    <td className="px-5 py-4 text-[13px] font-normal text-slate-600 md:px-6">
                      {formatDate(reconciliation.created_at?.slice(0, 10))}
                    </td>

                    <td className={`px-5 py-4 text-right text-[13px] font-medium md:px-6 ${
                      isNegativeBalance(reconciliation.starting_balance)
                        ? 'text-rose-600'
                        : 'text-slate-800'
                    }`}>
                      {formatReconciliationBalance(reconciliation.starting_balance)}
                    </td>

                    <td className={`px-5 py-4 text-right text-[13px] font-medium md:px-6 ${
                      isNegativeBalance(reconciliation.ending_balance)
                        ? 'text-rose-600'
                        : 'text-slate-800'
                    }`}>
                      <span className="inline-flex items-center justify-end gap-2">
                        {formatReconciliationBalance(reconciliation.ending_balance)}
                        {isUnbalanced && (
                          <span
                            className="inline-flex h-5 w-5 items-center justify-center text-amber-700"
                            title={`Out of balance by ${formatReconciliationBalance(
                              Number(reconciliation.ending_balance || 0)
                              - Number(reconciliation.book_balance || 0)
                            )}`}
                            aria-label="Out of balance"
                          >
                            <SafeIcon icon={FiAlertTriangle} className="text-[17px]" />
                          </span>
                        )}
                      </span>
                    </td>

                    <td className="px-5 py-3.5 md:px-6">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(reconciliation)}
                          disabled={!isEditable || isEditing || isDeleting}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-blue-300 bg-white text-blue-600 transition-all hover:border-blue-500 hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 disabled:opacity-60"
                          title={isUnbalanced
                            ? 'Open manual reconciliation'
                            : 'Edit reconciliation'}
                          aria-label={isUnbalanced
                            ? 'Open manual reconciliation'
                            : 'Edit reconciliation'}
                        >
                          {isEditing ? (
                            <SafeIcon icon={FiLoader} className="animate-spin text-[15px]" />
                          ) : (
                            <SafeIcon icon={FiEdit2} className="text-[15px]" />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(reconciliation)}
                          disabled={!isEditable || isDeleting || isEditing}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-blue-300 bg-white text-blue-600 transition-all hover:border-rose-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300 disabled:opacity-60"
                          title={isUnbalanced
                            ? 'Delete unfinished reconciliation'
                            : 'Reverse reconciliation'}
                          aria-label={isUnbalanced
                            ? 'Delete unfinished reconciliation'
                            : 'Reverse reconciliation'}
                        >
                          {isDeleting ? (
                            <SafeIcon icon={FiLoader} className="animate-spin text-[15px]" />
                          ) : (
                            <SafeIcon icon={FiTrash2} className="text-[15px]" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <ReconciliationWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onSuccess={loadData}
      />

      <ReconciliationDeleteWarning
        reconciliation={deleteCandidate}
        isDeleting={Boolean(deletingId)}
        onCancel={() => {
          if (!deletingId) setDeleteCandidate(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
}